'use client';

import * as React from 'react';
import { disabledTokens } from '@federa/utils/styles/interaction-tokens';
import { INLINE_FLEX_CENTER_STYLE } from '@federa/const/style-flex-presets';
import { FOCUS_VISIBLE_SELECTOR } from '@federa/const/focus-visible';
import { typography, fontFamilyCSS } from '../css-refs.typography.ts';
import { spacing } from '../css-refs.spacing.ts';
import { boxShadow } from '../css-refs.effects.ts';
import { layoutTokens } from '../css-refs.layout-tokens.ts';
import { disabledOpacity, TRANSITION_BASE } from '../common/layout-styles.ts';
import { cursor } from '../common/style-primitives.ts';
import { active } from '../css-refs.interaction.ts';
import { composeHandler, setFlagThen } from './compose-handler.ts';
import { BUTTON_HOVER_FILL_NONE, BUTTON_RADIUS_TOKENS, BUTTON_SIZE_FACETS, BUTTON_VARIANT_FACETS } from './contracts/button.ts';
import type { ButtonChildrenLayout, ButtonProps, ButtonRadius, ButtonVariant, ButtonVariantFacets } from '@federa/types/ui-button';
import type { ComponentSize } from '@federa/types/ui-design-tokens';
import type { ButtonIconName } from '@federa/types/ui-icons';

// STYLE HELPERS -- every per-variant and per-size value is READ from the contract records in `./contracts/button.ts`;
// nothing below decides a variant's appearance, so a new variant cannot half-land here.

/** Project a variant's resting facets onto CSS. The `-webkit-` backdrop filter is written from the SAME facet, so the
 * prefixed pair cannot drift apart the way two hand-written properties could. */
function getVariantRestingStyles(facets: ButtonVariantFacets): React.CSSProperties {
	return {
		backgroundColor: facets.background,
		color: facets.foreground,
		border: facets.border,
		...(facets.backdropFilter !== null && {
			backdropFilter: facets.backdropFilter,
			WebkitBackdropFilter: facets.backdropFilter,
		}),
		...facets.extra,
	};
}

/**
 * The BEHAVIOURAL half of a button's resting style -- what the control IS, not what its box looks like: the pointer
 * affordance, the disabled contract, the transition and the text-selection guard. Every caller gets these, including
 * one that owns its own box.
 *
 * THE OUTLINE SUPPRESSION IS NO LONGER ONE OF THEM, and the asymmetry that removed it is the whole bug: `outline:
 * 'none'` was written UNCONDITIONALLY here while the ring that replaces it (`buttonFocusStyles`) is drawn only when
 * React state says `isFocused`. An outline is invisible at rest, so suppressing it at rest bought nothing and cost
 * everything -- any path where that state does not arrive (a runtime with no `:focus-visible`, a static render, focus
 * moved programmatically, a caller whose own `style` reaches the node first) left a button that swallowed the browser's
 * indicator and drew none of its own. Suppression now rides `buttonFocusStyles` alone, at the one instant it has
 * something to suppress, so the base sheet's `:focus-visible` default is what shows through if the ring ever does not
 * (operator's accessibility sweep of the mission viewer, 2026-09-14, HIGH, WCAG 2.4.7).
 */
function getButtonBehaviourStyles(isDisabled: boolean): React.CSSProperties {
	return {
		cursor: isDisabled ? disabledTokens.cursor : cursor.pointer,
		opacity: disabledOpacity(isDisabled),
		pointerEvents: isDisabled ? disabledTokens.pointerEvents : 'auto',
		transition: TRANSITION_BASE,
		userSelect: 'none',
		// The typeface rides the BEHAVIOUR half, not the label box: a `content` button that suppresses the label chrome
		// otherwise falls to the user agent's button family (Arial, measured against the page's own stack in the
		// four-persona audit, 2026-09-01) -- the family is a property of every button, the label box is not.
		fontFamily: fontFamilyCSS.sans,
	};
}

/**
 * The BOX half -- the label-shaped chrome: an inline-flex row that centres and gaps its icon/label run, the corner
 * (the pill unless the caller names a squarer one off `BUTTON_RADIUS_TOKENS`, iss-ad97d4c3), the label typography and
 * the no-wrap guard that keeps a label on one line.
 *
 * SUPPRESSED UNDER `childrenLayout: 'content'`, together with the size facets, and that is what makes the second half
 * of that contract real. A caller declaring it owns the content's layout does not own it while the primitive still
 * forces `display: inline-flex`, a pill radius, a fixed height and `white-space: nowrap` -- a grid row collapses, a
 * multi-line body renders on one line, and a full-bleed hit area draws rounded corners. Cancelling those at the call
 * site is what kept this class raw: it means writing literal box values back over the primitive, which is the
 * hardcoded-style bypass `DS-HARDCODED-STYLES-002` blocks at HIGH.
 *
 * `fullWidth` stays outside this split because it is a caller INSTRUCTION rather than label chrome, and it is the one
 * box property a content button still legitimately asks the primitive for.
 */
function getButtonLabelBoxStyles(radius: ButtonRadius): React.CSSProperties {
	return {
		...INLINE_FLEX_CENTER_STYLE,
		gap: spacing[2],
		fontWeight: typography.fontWeight.medium,
		borderRadius: BUTTON_RADIUS_TOKENS[radius],
		whiteSpace: 'nowrap',
	};
}

function getButtonIconOnlyStyles(size: ComponentSize): React.CSSProperties {
	return {
		width: BUTTON_SIZE_FACETS[size].height,
		padding: spacing[0],
	};
}

/**
 * The hover layer, with the caller's override folded in at the ONE point it can be honoured. `hoverFill` replaces the
 * variant's `hoverBackground`; `BUTTON_HOVER_FILL_NONE` removes the layer entirely, shadow included, which is the only
 * way a control that must NOT repaint under the pointer can say so once its resting fill came from `style`.
 */
function getButtonHoverStyles(facets: ButtonVariantFacets, hoverFill: string | undefined): React.CSSProperties {
	if (hoverFill === BUTTON_HOVER_FILL_NONE) return {};
	return {
		backgroundColor: hoverFill ?? facets.hoverBackground,
		// CONDITIONAL SPREAD, not `hoverShadow ? sm : undefined`. This bag is spread OVER the caller's `style`, and an
		// `undefined` in a React style object DELETES the property it lands on -- the same mechanism that made a
		// `default` button go transparent on hover under the old partial facet map. A non-elevating variant answers
		// "hover raises no shadow", never "hover erases the caller's shadow", and a tab trigger carrying a resting
		// elevation for its selected state is exactly the caller that could tell the difference.
		...(facets.hoverShadow && { boxShadow: boxShadow.sm }),
	};
}

function getButtonPressedStyles(): React.CSSProperties {
	return {
		transform: `scale(${active.scale.medium})`,
	};
}

const buttonFocusStyles: React.CSSProperties = {
	// The design system's OWN ring colour, not the subtle overlay: a 20% white ring composited over a white surface
	// measured 1.00:1 -- keyboard focus was invisible on every light-surface button (four-persona audit, 2026-09-01).
	boxShadow: `0 0 0 ${layoutTokens.focus.ring.default.width} ${layoutTokens.focus.ring.default.color}`,
	outline: 'none',
};

function renderButtonContent(options: {
	loading: boolean;
	icon: ButtonIconName | undefined;
	iconBefore: ButtonIconName | undefined;
	iconAfter: ButtonIconName | undefined;
	children: React.ReactNode;
	childrenLayout: ButtonChildrenLayout;
	renderIcon: (name: ButtonIconName) => React.ReactNode;
}): React.ReactNode {
	const { loading, icon, iconBefore, iconAfter, children, childrenLayout, renderIcon } = options;
	// The `<span>` groups a TEXT RUN against the icon slots. Under `content` the children are the caller's own boxes and
	// the `<button>` is what they position and lay out against, so the wrapper is dropped -- see `ButtonChildrenLayout`.
	const body = childrenLayout === 'content' ? children : children && <span>{children}</span>;
	/** One icon SLOT: empty while the button is loading -- the spinner owns the row then -- and empty when unset. All
	 *  three slots answer to the same rule, so a slot that kept its glyph under `loading` would be the odd one out. */
	const iconSlot = (name: ButtonIconName | undefined): React.ReactNode => (!loading && name ? renderIcon(name) : null);
	return (
		<>
			{loading && renderIcon('loading')}
			{iconSlot(iconBefore)}
			{iconSlot(icon)}
			{body}
			{iconSlot(iconAfter)}
		</>
	);
}

/**
 * The whole style stack for one render, EXPORTED because hover, press and focus are React state a static render cannot
 * reach -- the same reason `getCloseButtonStyles` is exported, and the channel
 * `components/primitives/__tests__/interaction-affordances.test.ts` drives to prove the state axis is real.
 */
export function computeButtonStyles(options: {
	variant: ButtonVariant;
	size: ComponentSize;
	isDisabled: boolean;
	isIconOnly: boolean;
	fullWidth: boolean;
	isHovered: boolean;
	isPressed: boolean;
	isFocused: boolean;
	customStyle?: React.CSSProperties;
	hoverFill?: string;
	childrenLayout?: ButtonChildrenLayout;
	/** The label box's corner; the pill when absent. */
	radius?: ButtonRadius;
}): React.CSSProperties {
	const { variant, size, isDisabled, isIconOnly, fullWidth, isHovered, isPressed, isFocused, customStyle, hoverFill } = options;
	const variantFacets = BUTTON_VARIANT_FACETS[variant];
	// A `content` button owns its own box: it gets behaviour, fill and interaction state, and no label chrome.
	const ownsBox = options.childrenLayout === 'content';
	const boxStyles = ownsBox ? {} : { ...getButtonLabelBoxStyles(options.radius ?? 'full'), ...BUTTON_SIZE_FACETS[size] };
	const iconOnlyStyles = isIconOnly && !ownsBox ? getButtonIconOnlyStyles(size) : {};
	const hoverStyles = isHovered && !isDisabled ? getButtonHoverStyles(variantFacets, hoverFill) : {};
	const pressedStyles = isPressed && !isDisabled ? getButtonPressedStyles() : {};
	const focusStyles = isFocused && !isDisabled ? buttonFocusStyles : {};

	return {
		...getButtonBehaviourStyles(isDisabled),
		...boxStyles,
		width: fullWidth ? '100%' : 'auto',
		...getVariantRestingStyles(variantFacets),
		...iconOnlyStyles,
		...customStyle,
		...hoverStyles,
		...pressedStyles,
		...focusStyles,
	};
}

/**
 * The three interaction flags and the six handlers that drive them, as one named hook.
 *
 * A HOOK RATHER THAN A PLAIN HELPER because the flags ARE the button's own state: the `useState` calls have to run in a
 * render body, and keeping all three here fixes their order by construction. Every handler is still
 * `composeHandler(own, caller)` in the same order it always was -- the primitive's own state update first, the caller's
 * handler second -- so the composed behaviour is unchanged; only its home moved, to keep the render function inside the
 * REACT per-function cap.
 */
function useButtonInteraction(callerHandlers: {
	onFocus?: React.FocusEventHandler<HTMLButtonElement>;
	onBlur?: React.FocusEventHandler<HTMLButtonElement>;
	onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
	onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
	onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
	onMouseUp?: React.MouseEventHandler<HTMLButtonElement>;
}) {
	const [isHovered, setIsHovered] = React.useState(false);
	const [isPressed, setIsPressed] = React.useState(false);
	const [isFocused, setIsFocused] = React.useState(false);
	const { onBlur } = callerHandlers;

	const interaction = {
		onMouseEnter: setFlagThen(setIsHovered, true, callerHandlers.onMouseEnter),
		onMouseLeave: composeHandler(() => {
			setIsHovered(false);
			setIsPressed(false);
		}, callerHandlers.onMouseLeave),
		onMouseDown: setFlagThen(setIsPressed, true, callerHandlers.onMouseDown),
		onMouseUp: setFlagThen(setIsPressed, false, callerHandlers.onMouseUp),
		// KEYBOARD focus only. `focus` fires on a mouse click too, and the ring then stayed on the bell and the sidebar
		// user button until the next blur (operator 2026-09-03). `:focus-visible` is the browser's own keyboard-vs-pointer
		// verdict; a runtime without it (a static render) reads as not focused, which is the same as the server pass.
		onFocus: composeHandler(
			(event: React.FocusEvent<HTMLButtonElement>) =>
				setIsFocused(typeof event.currentTarget.matches === 'function' && event.currentTarget.matches(FOCUS_VISIBLE_SELECTOR)),
			callerHandlers.onFocus,
		),
		onBlur: setFlagThen(setIsFocused, false, onBlur),
	};

	return { isHovered, isPressed, isFocused, interaction };
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			children,
			variant = 'primary',
			size = 'md',
			// The corner is a style input like `size`, pulled out so it never reaches the native element as an attribute.
			radius = 'full',
			loading = false,
			iconBefore,
			iconAfter,
			icon,
			childrenLayout = 'label',
			fullWidth = false,
			disabled: disabledProp,
			className,
			style,
			// PULLED OUT FOR TWO REASONS. It feeds the style computation below, and it must not survive into `props`:
			// every unclaimed prop is forwarded onto the native element, and React would write an unknown `hoverfill`
			// attribute onto the DOM the way it does for any non-standard lower-cased prop it is handed.
			hoverFill,
			type = 'button',
			renderIconFn,
			// THE SIX EVENTS THIS BUTTON ALSO DRIVES ITS OWN STATE FROM, PULLED OUT OF THE REST ON PURPOSE. They used to
			// ride `props` into the trailing spread, which overwrote every composed handler below it: `handleFocus`
			// called `props.onFocus` and then set the focus ring, and the spread threw that whole function away -- so a
			// caller passing `onFocus` got their handler and silently lost the ring, and the four mouse handlers lost
			// their hover and press state the same way. Destructured out, `props` cannot carry them and the spread's
			// position stops deciding anything. See `composeHandler`.
			onFocus,
			onBlur,
			onMouseEnter,
			onMouseLeave,
			onMouseDown,
			onMouseUp,
			...props
		},
		ref,
	) => {
		const isDisabled = disabledProp || loading;
		const isIconOnly = !!icon && !children;

		const { isHovered, isPressed, isFocused, interaction } = useButtonInteraction({
			onFocus,
			onBlur,
			onMouseEnter,
			onMouseLeave,
			onMouseDown,
			onMouseUp,
		});

		const buttonStyles = computeButtonStyles({
			variant,
			size,
			isDisabled,
			isIconOnly,
			fullWidth,
			isHovered,
			isPressed,
			isFocused,
			customStyle: style,
			hoverFill,
			childrenLayout,
			radius,
		});

		const renderIcon = (name: ButtonIconName) => {
			if (!renderIconFn) return null;
			return renderIconFn(name, size);
		};

		const buttonContent = renderButtonContent({
			loading,
			icon,
			iconBefore,
			iconAfter,
			children,
			childrenLayout,
			renderIcon,
		});

		return (
			<button
				ref={ref}
				type={type}
				disabled={isDisabled}
				// THE ARIA HALF OF `loading`, which the visual half shipped without. A loading button is `disabled`, so
				// assistive technology announced it as unavailable and never as IN PROGRESS -- indistinguishable from a
				// button the user is simply not allowed to press. Written ahead of the caller's spread so their own
				// value still wins, and `|| undefined` so a resting button carries no attribute at all rather than
				// `aria-busy="false"`.
				aria-busy={loading || undefined}
				className={className}
				style={buttonStyles}
				{...interaction}
				{...props}
			>
				{buttonContent}
			</button>
		);
	},
);

Button.displayName = 'Button';
